import React, { Component } from "react";
import classnames from "classnames";
import PropTypes from "prop-types";
import "./doughnut.scss";

const getStrokeDashOffset = (val = 0, circumference) =>
  Math.round(((100 - Math.min(val, 100)) / 100) * circumference);
const getMarkerRotation = (val = 0) => Math.round((val / 100) * 360);

const inView = (element, elementHeight) => {
  if (element) {
    const windowHeight = window.innerHeight;
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollPosition = scrollY + windowHeight;
    const elementPosition = element.getBoundingClientRect().top + scrollY + elementHeight;

    if (scrollPosition > elementPosition) {
      return true;
    }
  }

  return false;
};

class Doughnut extends Component {
  constructor(props) {
    super(props);

    const { shouldAnimate } = props;

    this.doughnutRef = null;
    this.setDoughnutRef = element => {
      this.doughnutRef = element;
    };

    this.setUpComponent = this.setUpComponent.bind(this);
    this.animate = this.animate.bind(this);

    const componentState = this.setUpComponent();
    const { markerRotationValue, strokeDashoffsetValue, circumference, radius } = componentState;

    this.state = {
      markerRotationValue: shouldAnimate ? 0 : markerRotationValue,
      markerRotationAnimationValue: markerRotationValue,
      strokeDashoffsetValue: shouldAnimate ? circumference : strokeDashoffsetValue,
      strokeDashoffsetAnimationValue: strokeDashoffsetValue,
      circumference,
      radius,
    };
  }

  componentDidMount() {
    const { shouldAnimate } = this.props;
    if (!shouldAnimate) {
      return;
    }

    document.addEventListener("scroll", this.animate);

    setTimeout(() => {
      this.animate();
    }, 200);
  }

  componentDidUpdate(prevProps) {
    const { size, progress } = this.props;

    if (prevProps.size !== size || prevProps.progress !== progress) {
      const updatedState = this.setUpComponent();
      this.setState(state => ({ ...state, ...updatedState }));
    }
  }

  componentWillUnmount() {
    document.removeEventListener("scroll", this.animate);
  }

  setUpComponent() {
    const { progress, size } = this.props;

    let radius = 38;
    if (size === "medium") {
      radius = 82;
    } else if (size === "large") {
      radius = 112;
    }

    const circumference = Math.round(Math.PI * radius * 2);

    const strokeDashOffset = getStrokeDashOffset(progress, circumference);
    const markerRotation = getMarkerRotation(progress);

    return {
      markerRotationValue: markerRotation,
      strokeDashoffsetValue: strokeDashOffset,
      circumference,
      radius,
    };
  }

  animate() {
    this.doughnutHeight = this.doughnutRef && this.doughnutRef.clientHeight;

    if (inView(this.doughnutRef, this.doughnutHeight)) {
      this.setState(state => {
        return {
          ...state,
          strokeDashoffsetValue: state.strokeDashoffsetAnimationValue,
          markerRotationValue: state.markerRotationAnimationValue,
        };
      });
    }
  }

  render() {
    const {
      className,
      lineWidth,
      shouldAnimate,
      animationDuration,
      children,
      size,
      hideMarker,
      markerType,
    } = this.props;

    const { strokeDashoffsetValue, markerRotationValue, circumference, radius } = this.state;
    const { progress } = this.props;

    const easing = "cubic-bezier(0.19, 1, 0.22, 1)";
    const pathTransitionStyles = shouldAnimate
      ? `stroke-dashoffset ${animationDuration} ${easing}`
      : undefined;
    const markerTransitionStyles = shouldAnimate
      ? `transform ${animationDuration} ${easing}`
      : undefined;

    const viewPortSize = radius * 2 + 20;

    const classes = classnames(
      "doughnut",
      {
        "doughnut--medium": size === "medium",
        "doughnut--large": size === "large",
      },
      className
    );

    return (
      <div className={classes} ref={this.setDoughnutRef}>
        <svg viewBox={`-10 -10 ${viewPortSize} ${viewPortSize}`} style={{ overflow: "visible" }}>
          <circle
            className="doughnut__track"
            strokeLinecap="round"
            strokeDasharray={circumference}
            cx={radius}
            cy={radius}
            r={radius}
            strokeWidth={lineWidth}
            fill="none"
          />
          {progress !== 0 && (
            <circle
              transform={`rotate(-90 ${radius} ${radius})`}
              cx={radius}
              cy={radius}
              r={radius}
              strokeDasharray={circumference}
              strokeWidth={lineWidth}
              strokeDashoffset={circumference}
              strokeLinecap="round"
              fill="none"
              style={{ strokeDashoffset: strokeDashoffsetValue, transition: pathTransitionStyles }}
              onTransitionEnd={() => {}}
              className="doughnut__path"
            />
          )}
          {!hideMarker && (
            <circle
              className="doughnut__marker"
              strokeWidth={lineWidth / 2}
              cx={radius}
              cy={0}
              r={lineWidth / 1.5}
              fill={markerType === "SOLID" ? "currentColor" : "#fff"}
              style={{
                transform: `rotate(${markerRotationValue}deg)`,
                transition: markerTransitionStyles,
                transformOrigin: `${radius}px ${radius}px`,
              }}
            />
          )}
        </svg>
        <span className="doughnut__text">{children}</span>
      </div>
    );
  }
}

Doughnut.defaultProps = {
  lineWidth: 8,
  progress: 0,
  shouldAnimate: true,
  animationDuration: "3s",
  size: "small",
  markerType: "CIRCLE",
};

Doughnut.propTypes = {
  className: PropTypes.string,
  lineWidth: PropTypes.number,
  progress: PropTypes.number,
  shouldAnimate: PropTypes.bool,
  animationDuration: PropTypes.string,
  size: PropTypes.oneOf(["small", "medium", "large"]),
  hideMarker: PropTypes.bool,
  markerType: PropTypes.oneOf(["CIRCLE", "SOLID"]),
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
};

export default Doughnut;
